import { NgTemplateOutlet, AsyncPipe } from '@angular/common';
import type { SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  output,
  inject,
  input,
} from '@angular/core';
import type {
  AbstractControl,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
} from '@angular/forms';
import { UntypedFormArray, UntypedFormBuilder, Validators } from '@angular/forms';

import { Store } from '@ngrx/store';
import { deepEqual } from 'fast-equals';
import { merge as observableMerge, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil, takeWhile } from 'rxjs/operators';

import { AccountSelectors } from '@core/account-data';
import {
  emailValidator,
  filterBoolean,
  phoneNumberValidatorFn,
  softUrlValidator,
  URL_PROTOCOL_PATTERN,
} from '@core/common-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import type {
  CandidateInformationForm,
  CandidateInformationFormCustomGroup,
} from '@core/offer-candidate-types';
import type { IconName } from '@core/user-interface-legacy';
import {
  BoxComponent,
  ButtonComponent,
  ButtonLoadingDirective,
  ClipboardCopy,
  DividerComponent,
  GroupComponent,
  HeadingComponent,
  IconButtonComponent,
  ProtectedControlDirective,
  providersForControlsContext,
  RtTooltip,
  SocialMediaPipe,
} from '@core/user-interface-legacy';
import { asViewRef, generateUuid, objectKeys } from '@core/utils-legacy';

import { CandidateInformationEditorGroupComponent } from './candidate-information-editor-group/candidate-information-editor-group.component';

/* eslint-disable max-lines */

export enum Group {
  Emails = 'emails',
  Phones = 'phones',
  Links = 'links',
  SocialLinks = 'socialLinks',
  CustomFields = 'customFields',
}

@Component({
  selector: 'rt-add-candidate-profile-information',
  templateUrl: './add-candidate-information-editor.component.html',
  styleUrls: ['./add-candidate-information-editor.component.less'],
  providers: [...providersForControlsContext('information')],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    GroupComponent,
    ButtonComponent,
    ButtonLoadingDirective,
    BoxComponent,
    HeadingComponent,
    NgTemplateOutlet,
    DividerComponent,
    IconButtonComponent,
    RtTooltip,
    CandidateInformationEditorGroupComponent,
    ProtectedControlDirective,
    AsyncPipe,
    TranslatePipe,
    SocialMediaPipe,
  ],
})
export class AddCandidateInformationEditorComponent {
  private readonly i18n = inject(I18nService);
  private readonly fb = inject(UntypedFormBuilder);
  private readonly clipboardCopy = inject(ClipboardCopy);
  private readonly store = inject(Store);
  protected readonly cdRef = inject(ChangeDetectorRef);

  public readonly icons: { [key in Group]: IconName } = {
    emails: 'mail',
    phones: 'phone',
    socialLinks: 'share',
    links: 'link',
    customFields: 'description',
  };

  public readonly texts: {
    buttons: { [key: string]: string };
    placeholders: { [key: string]: string };
    labels: { [key: string]: string };
  } = {
    buttons: {
      add: this.i18n.translate('app.common.add'),
      addCustom: this.i18n.translate('app.candidate.add_custom_field_item'),
    },
    placeholders: {
      socialLinks: this.i18n.translate('app.candidate.add_social_link_placeholder'),
      links: this.i18n.translate('app.candidate.add_link_placeholder'),
      customFields: this.i18n.translate('app.candidate.add_custom_field_placeholder'),
    },
    labels: {
      emails: this.i18n.translate('app.common.email'),
      phones: this.i18n.translate('app.candidate.phone_short'),
      socialLinks: this.i18n.translate('app.candidate.socials'),
      links: this.i18n.translate('app.candidate.links'),
    },
  };

  public readonly validatorsMap: { [key: string]: ValidatorFn } = {
    phones: phoneNumberValidatorFn,
    emails: Validators.compose([emailValidator, Validators.maxLength(200)])!,
    links: softUrlValidator,
    socialLinks: softUrlValidator,
  };

  public readonly information = input<CandidateInformationForm>(undefined!);
  public readonly editing = input<boolean>(undefined!);
  public readonly editable = input<boolean>(undefined!);
  public readonly boxStyle = input<'default' | 'transparent'>('default');
  public readonly permanentlyEditable = input<boolean>(false);
  public readonly visibleFields = input<string[]>([
    'emails',
    'phones',
    'socialLinks',
    'links',
    'customFields',
  ]);
  public readonly validationErrors = input<{
    emails: string;
  }>({ emails: '' });
  @Input() public loading: boolean;
  @Input() public actionsVisible: boolean = true;
  @Input() public showHeading: boolean = true;

  public save = output<CandidateInformationForm>();
  public edit = output<void>();
  public discard = output<void>();
  public switchToEmailTab = output<void>();
  public valueChanges = output<void>();

  public form: UntypedFormGroup;
  public focusedControl: AbstractControl | undefined;
  public focusedControlPath: Array<string | number> | undefined;
  public countryCode: string | null;

  private informationPristineData: CandidateInformationForm;

  private ngOnDestroy$ = new Subject<void>();

  constructor() {
    this.createForm(null, true);
    this.store
      .select(AccountSelectors.currentCompany)
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$))
      .subscribe(({ countryCode }) => (this.countryCode = countryCode));
  }

  public ngOnInit(): void {
    observableMerge(this.form.statusChanges.pipe(distinctUntilChanged()), this.form.valueChanges)
      .pipe(
        debounceTime(1),
        takeWhile(() => !asViewRef(this.cdRef).destroyed),
      )
      .subscribe(_ => {
        if (this.focusedControlPath) {
          this.focusedControl = this.form.get(this.focusedControlPath)!;
        }

        this.valueChanges.emit();
        this.cdRef.detectChanges();
      });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const hasError =
      changes.validationErrors &&
      changes.validationErrors.currentValue &&
      changes.validationErrors.currentValue.emails.length;

    if ('information' in changes) {
      const informationDidChanged = !deepEqual(
        changes.information.currentValue,
        changes.information.previousValue,
      );

      if (informationDidChanged) {
        this.informationPristineData = changes.information.currentValue;
        this.setForm(changes.information.currentValue, !this.permanentlyEditable());
      }
    }

    if ('loading' in changes) {
      if (changes.loading.currentValue) {
        this.form.disable();
      } else if (hasError) {
        this.form.enable();
      }
    }

    if ('editing' in changes) {
      const editing = changes.editing.currentValue;

      if ((editing && this.form.enabled) || (!editing && !this.form.enabled)) {
        return;
      }

      if (editing) {
        this.form.enable();

        [Group.Emails, Group.Phones]
          .filter(groupId => (<UntypedFormArray>this.form.get([groupId])).length === 0)
          .forEach(groupId => this.addControl([groupId], false, !this.focusedControlPath));

        this.form.markAsPristine();
        this.form.updateValueAndValidity();
      } else {
        this.setForm(this.informationPristineData, true);
        this.form.markAsPristine();
        this.focusedControlPath = undefined;
      }
    }
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public canShowAddButton(groupId: string): boolean {
    return (
      this.editable() &&
      ((<UntypedFormArray>this.form.get([groupId])).length === 0 || this.form.enabled)
    );
  }

  public addControl(
    path: Array<string | number>,
    enableForm: boolean = false,
    setFocus: boolean = false,
  ): void {
    const [groupId] = path;

    if (this.form.get([`${groupId}`])) {
      const target = this.form.get(path);

      if (target instanceof UntypedFormArray) {
        const control = this.fb.control(
          {
            value: null,
            disabled: this.form.disabled,
          },
          this.validatorsMap[groupId],
        );

        if (setFocus) {
          this.focusedControlPath = [...path, target.length];
        }

        target.push(control);
        target.markAsDirty();
        target.updateValueAndValidity();

        if (enableForm && !this.form.enabled) {
          this.edit.emit();
        }
      }
    }
  }

  public removeControl(path: Array<string | number>): void {
    const [groupId] = path;

    if (this.form.contains(`${groupId}`)) {
      const target = this.form.get(path);

      if (target && target.parent instanceof UntypedFormArray) {
        const { parent } = target;

        parent.removeAt(parent.controls.findIndex(control => control === target));
        parent.markAsDirty();
        parent.updateValueAndValidity();
      }
    }
  }

  public reorderControl(path: Array<string | number>): void {
    this.focusedControlPath = undefined;
    this.focusedControl = undefined;

    const control = this.form.get(path)!;
    control.markAsDirty();
    control.updateValueAndValidity();
  }

  public stripValue(data: CandidateInformationForm): CandidateInformationForm {
    const valueFilter = (val: any) => val !== null && val !== '';

    return objectKeys(data).reduce((agg, key) => {
      if (key === Group.CustomFields) {
        return {
          ...agg,
          [key]: agg[key]
            .map(customField => ({
              ...customField,
              values: customField.values.filter(valueFilter),
            }))
            .filter(customField => typeof customField.label === 'string'),
        };
      } else if (Array.isArray(agg[key])) {
        agg[key] = (<any[]>agg[key]).filter(valueFilter);
      }

      return agg;
    }, data);
  }

  public checkUrl(control: AbstractControl): void {
    if (control.value && !URL_PROTOCOL_PATTERN.test(control.value)) {
      control.setValue(`http://${control.value}`);
      control.updateValueAndValidity();
    }
  }

  public getGroupControls(groupName: string): UntypedFormControl[] {
    return <UntypedFormControl[]>this.getGroup(groupName).controls;
  }

  public getGroup(groupName: string): UntypedFormArray {
    return <UntypedFormArray>this.form.get([groupName]);
  }

  public trackByField(index: number): number {
    return index;
  }

  public copyToClipboard(value: string): void {
    this.clipboardCopy.fromString(value);
  }

  public setForm(data?: CandidateInformationForm, disabled: boolean = true): void {
    const emails = (data && data.emails) || this.form.get(Group.Emails)!.value;
    const phones = (data && data.phones) || this.form.get(Group.Phones)!.value;
    const links = (data && data.links) || this.form.get('links')!.value;
    const socialLinks = (data && data.socialLinks) || this.form.get('socialLinks')!.value;
    const customFields = (data && data.customFields) || this.form.get('customFields')!.value;

    this.form.setControl(
      Group.Emails,
      this.createControlsArr(emails, disabled, this.validatorsMap.emails),
    );

    this.form.setControl(
      Group.Phones,
      this.createControlsArr(phones, disabled, this.validatorsMap.phones),
    );
    this.form.setControl(
      Group.Links,
      this.createControlsArr(links, disabled, this.validatorsMap.links),
    );
    this.form.setControl(
      Group.SocialLinks,
      this.createControlsArr(socialLinks, disabled, this.validatorsMap.links),
    );
    this.form.setControl(
      Group.CustomFields,
      this.fb.array(
        customFields.length
          ? customFields.map((customFieldData: CandidateInformationFormCustomGroup) =>
              this.createCustomGroup(customFieldData, disabled),
            )
          : [],
      ),
    );

    disabled ? this.form.disable() : this.form.enable();
  }

  private createForm(data: CandidateInformationForm | null, disabled: boolean = true): void {
    this.form = this.fb.group({
      emails: this.createControlsArr(
        (data && data.emails) || [],
        disabled,
        this.validatorsMap.emails,
      ),
      phones: this.createControlsArr(
        (data && data.phones) || [],
        disabled,
        this.validatorsMap.phones,
      ),
      links: this.createControlsArr((data && data.links) || [], disabled, this.validatorsMap.links),
      socialLinks: this.createControlsArr(
        (data && data.socialLinks) || [],
        disabled,
        this.validatorsMap.links,
      ),
      customFields: this.fb.array(
        data && data.customFields.length
          ? data.customFields.map(customFieldData =>
              this.createCustomGroup(customFieldData, disabled),
            )
          : [],
      ),
    });

    disabled ? this.form.disable() : this.form.enable();
  }

  private createCustomGroup(
    data: CandidateInformationFormCustomGroup = { label: null, values: [], id: generateUuid() },
    disabled: boolean = false,
  ): UntypedFormGroup {
    return this.fb.group({
      label: this.fb.control(
        {
          value: data.label,
          disabled,
        },
        Validators.required,
      ),
      values: this.createControlsArr(<string[]>data.values || [], disabled),
    });
  }

  private createControlsArr(
    data: string[],
    disabled: boolean = false,
    validator?: ValidatorFn,
  ): UntypedFormArray {
    return Array.isArray(data) && data.length >= 1
      ? this.fb.array(
          data.map(value =>
            this.fb.control(
              {
                value,
                disabled,
              },
              validator ? [validator] : undefined,
            ),
          ),
        )
      : this.fb.array([]);
  }
}
